/**
 * What the reader has asked for, and its address.
 *
 * Four weights, which schools count, where transport is measured to, which category is being looked at on its
 * own, and which area is selected. All of it belongs in the query string: a map
 * that says something is a link that says the same thing when it is opened
 * again, which is the property the rest of this project gets from
 * `--preset paper`.
 *
 * Only what differs from the manifest's own starting point is written, so a
 * default that later changes in `propmap/prod.py` is picked up rather than
 * pinned by an old link.
 */

import type { Manifest } from "./manifest.ts";

export interface State {
  /** slider per category, by layer name, on 0..weight_max */
  weights: Record<string, number>;
  /** which schools count: a key of the schools layer's phases */
  phase: string;
  /** a key of the transport layer's destinations, or null for its default */
  destination: string | null;
  /** the category being viewed on its own, or null for the blend */
  view: string | null;
  /** the selected area's LSOA code, or null */
  area: string | null;
  /** whether the panel's per-category detail lines are open */
  detail: boolean;
}

function phaseLayer(manifest: Manifest) {
  return manifest.layers.find((layer) => layer.phases !== null);
}

export function defaultState(manifest: Manifest): State {
  const weights: Record<string, number> = {};
  for (const layer of manifest.layers) weights[layer.name] = layer.start_weight;
  return {
    weights,
    phase: phaseLayer(manifest)?.phases?.default ?? "any",
    destination: null,
    view: null,
    area: null,
    detail: false,
  };
}

export function fromQuery(manifest: Manifest, search: string): State {
  const params = new URLSearchParams(search);
  const state = defaultState(manifest);
  for (const layer of manifest.layers) {
    const raw = params.get(layer.name);
    if (raw === null) continue;
    const value = Number(raw);
    if (Number.isFinite(value)) {
      state.weights[layer.name] = Math.min(Math.max(value, 0), manifest.blend.weight_max);
    }
  }
  const phase = params.get("phase");
  const phases = phaseLayer(manifest)?.phases;
  if (phase && phases && phases.order.includes(phase)) state.phase = phase;
  // Checked against the menu when it arrives, not here: the manifest carries
  // only the menu's address, and a key that is not on it falls back then.
  state.destination = params.get("to");
  const view = params.get("view");
  if (view && manifest.layers.some((layer) => layer.name === view)) state.view = view;
  state.area = params.get("area");
  state.detail = params.get("detail") === "1";
  return state;
}

export function toQuery(manifest: Manifest, state: State): string {
  const defaults = defaultState(manifest);
  const params = new URLSearchParams();
  for (const layer of manifest.layers) {
    const value = state.weights[layer.name] ?? 0;
    if (value !== defaults.weights[layer.name]) params.set(layer.name, String(Math.round(value)));
  }
  if (state.phase !== defaults.phase) params.set("phase", state.phase);
  if (state.destination) params.set("to", state.destination);
  if (state.view) params.set("view", state.view);
  if (state.area) params.set("area", state.area);
  if (state.detail) params.set("detail", "1");
  const query = params.toString();
  return query ? `?${query}` : "";
}

type Listener = (state: State, previous: State) => void;

/** The one place state changes, so every part of the interface sees the same one. */
export class Store {
  private current: State;
  private readonly manifest: Manifest;
  private readonly listeners: Listener[] = [];
  private writing = 0;

  constructor(manifest: Manifest, initial: State) {
    this.manifest = manifest;
    this.current = initial;
  }

  get state(): State {
    return this.current;
  }

  subscribe(listener: Listener): void {
    this.listeners.push(listener);
  }

  update(patch: Partial<State>): void {
    const previous = this.current;
    const next = { ...previous, ...patch };
    if (patch.weights) next.weights = { ...previous.weights, ...patch.weights };
    this.current = next;
    for (const listener of this.listeners) listener(next, previous);
    this.schedule();
  }

  /**
   * The address is rewritten on a timer rather than on every input event: a
   * dragged slider fires continuously, and browsers rate-limit history writes.
   */
  private schedule(): void {
    if (this.writing) window.clearTimeout(this.writing);
    this.writing = window.setTimeout(() => {
      this.writing = 0;
      const query = toQuery(this.manifest, this.current);
      window.history.replaceState(null, "", `${window.location.pathname}${query}`);
    }, 250);
  }
}
